import { createContext, useContext } from "react";
import type { LayerFilter } from "../../../../actions/Layers/constants";
import { defaultFilter } from "../../../../actions/Layers/constants";
import type { Tab } from "../../../../actions/Tenants/config/constantsTyped";
import type { RipeningPlanningProps } from "../../Ripening/hooks/useRipeningPlanning";
import type Field from "./Field";

export interface PersonalTabSettings {
    layer_type?: string;
    view_strategy?: string;
    max_days_in_facility?: string;
    planned_work_look_ahead_days?: string;
    show_layers_without_planned_work?: string;
    max_days_in_stage?: string;
    show_draft_reports?: string[];
    show_sent_reports?: string[];
    ordering?: string;
    limit?: number;
    /** Full list query (ordering + meta_filter); persisted so filters survive refresh */
    query?: Record<string, unknown>;
}

export interface SwitchTabContextProps {
    switchTab: (tab: any) => void;
    listLayersDebounced: (value: any) => void;
    /** Active list filter, kept in sync on every keystroke (the fetch itself is debounced). */
    filter: LayerFilter;
    /** Paginated overview result; always an object so consumers can read `.results` while loading. */
    layers: any;
    layersLoading: boolean;
    tabs: Tab[];
    sameCategoryTabs: Tab[];
    activeTab?: Tab;
    active_tab?: string;
    tab_category?: string;
    fields: Field[];
    /** Tab fields that are included in Excel download (excluded_from_download = false) */
    downloadFields: Field[];
    submitTabQuery: (query: any) => void;
    onUpdateOrder: (ordering: string) => void;
    /** Stream layers for export using the same filters/sort/search as the table. Uses downloadFields for select_fields.
     *  Caller controls limit/chunk_size and can apply a client-side safety cap. */
    streamLayersForExport: (exportOptions: {
        limit?: number;
        chunk_size?: number;
    }) => AsyncGenerator<{ results: any[]; count?: number; offset?: number; limit?: number }, void, unknown>;
    personalTabQuery: any;
    updatePersonalTabSettings: (key: string | object, value?: any) => void;
    personalTabSettings: PersonalTabSettings;
    normalizeUrl: () => void;
    selectedLayers: { [key: string]: boolean };
    setSelectedLayers: (value: any) => void;
    selectedLayersArray: string[];
    refreshTab: () => void;
    groupByProps?: any;
    setSearchQuery: (value?: any) => void;
    ripeningPlanning: RipeningPlanningProps;
}

// Lives apart from the provider so hooks can read the filter without importing
// the provider module, which would close an import cycle through the hooks barrel.
export const SwitchTabContext = createContext({
    switchTab: () => null,
    listLayersDebounced: () => null,
    filter: defaultFilter,
    layers: { results: [] },
    layersLoading: false,
    tabs: [],
    sameCategoryTabs: [],
    fields: [],
    downloadFields: [],
    submitTabQuery: () => null,
    onUpdateOrder: () => null,
    streamLayersForExport: async function* noopStream() { yield { results: [] }; },
    personalTabQuery: {},
    normalizeUrl: () => null,
    selectedLayers: {},
    setSelectedLayers: () => null,
    selectedLayersArray: [],
    personalTabSettings: {},
    updatePersonalTabSettings: () => null,
    refreshTab: () => null,
    setSearchQuery: () => null,
    ripeningPlanning: {
        getExtraFields: () => [],
        availableRipeningFields: [],
        ripeningField: null,
        selectRipeningField: () => null,
        ripeningFieldName: "",
    },
} as SwitchTabContextProps);

export const useSwitchTabContext = () => {
    const context = useContext(SwitchTabContext);
    if (!context) {
        throw new Error("useSwitchTabContext must be used within a SwitchTabProvider");
    }
    return context as SwitchTabContextProps;
};
